import { Component } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GetIfcSettingsRequest, IfcToJsonSettings, SaveIfcSettingsRequest, SituWarpApiClient, IfcToJsonFurnishingSettings, IIfcToJsonFurnishingSettings } from 'situ-warpapi-client';
import { ProductClassification, ProductType, ProductSpecification, Release } from 'situview-webapi-client';
import { AuthService, BreadcrumbService, StateService, ProductService, BannerService, BannerServiceStatus, ReleaseService, SearchThemeReplacementTagsOptions, ThemeService } from 'src/app/services';
import { FurnishingTags } from 'src/app/shared/models/furnishing-tags';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Guid } from 'guid-typescript';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-ifc-furnishings-config',
  templateUrl: './ifc-furnishings-config.component.html',
  styleUrls: ['./ifc-furnishings-config.component.less'],
})
export class IfcFurnishingsConfigComponent extends SituBaseComponent {
  furnishingTagType = FurnishingTags;
  furnishingTagKeyValues = Object.keys(FurnishingTags).filter(x => typeof FurnishingTags[x as keyof FurnishingTags] === "string").map(key => ({ key: key, value: (FurnishingTags as any)[key] })).sort((a, b) => a.key.localeCompare(b.key));

  settings: IfcToJsonSettings;
  furnishings: string[] = [];

  productClassifications?: ProductClassification[];
  productTypes?: ProductType[];
  productSpecifications?: ProductSpecification[];
  productNumbers: string[] = ['', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  isNewFurnishingFeed = true;

  filteredTags: string[] | undefined;
  showOnlyDeprecated = false;

  furnishingSettings: ExtendedIfcToJsonFurnishingSettings[] = [];
  filteredFurnishingSettings: ExtendedIfcToJsonFurnishingSettings[] = [];
  pagedFurnishingSettings: ExtendedIfcToJsonFurnishingSettings[] = [];
  furnishingSettingsPageIndex = 0;

  newFurnishingSettings: ExtendedIfcToJsonFurnishingSettings[] = [];
  filteredNewFurnishingSettings: ExtendedIfcToJsonFurnishingSettings[] = [];
  pagedNewFurnishingSettings: ExtendedIfcToJsonFurnishingSettings[] = [];
  newFurnishingSettingsPageIndex = 0;

  artifactId: Guid | undefined;

  readonly pageSize = 200;

  private _loading = false;
  get loading() {
    return this._loading;
  }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(this._loading);
  }

  get settingsjson() {
    return JSON.stringify(this.settings);
  }

  get isFurnishingSettingsDirty() {
    return JSON.stringify(this.settings.furnishingSettings) != JSON.stringify(this.furnishingSettings) || this.newFurnishingSettings.some(x => x.furnishingTag !== undefined);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private warpApiClient: SituWarpApiClient,
    private productService: ProductService,
    private themeService: ThemeService,
    private releaseService: ReleaseService,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private bannerService: BannerService) {
      super(authService, stateService);
      this.settings = this.getNewSettings();
      
      this.getProductClassifications()
      this.getProductTypes()
      this.getProductSpecifications()
  }

  protected onSelectedCompanyChange(): void {
    this.loadSettings(this.selectedCompany?.code!);

    this.loadReleases();
  }

  ngOnInit(): void {
    super.ngOnInit();
    
    this.filteredReleases = this.releaseControl.valueChanges.pipe(
      map((value) => {
        return this.filterReleases(value);
      })
    );
  }

  filterTypesByClassification(furnishingSetting: any): ProductType[] {
    if (this.productTypes && furnishingSetting && furnishingSetting.productClassificationId) {
      return this.productTypes
      .filter(item => item.productClassificationId === furnishingSetting.productClassificationId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      return [];
    }
  }

  clearFurnishingSettings(furnishingSetting: IfcToJsonFurnishingSettings) {
    furnishingSetting.productClassificationId = undefined;
    furnishingSetting.productTypeId = undefined;
    furnishingSetting.productSpecificationIds = [];
    furnishingSetting.productNumber = undefined;
    furnishingSetting.productFacingFlipped = furnishingSetting.furnishingName?.includes("(facing flipped)") ?? false;
    furnishingSetting.productHandednessFlipped = furnishingSetting.furnishingName?.includes("(hand flipped)") ?? false;
    furnishingSetting.doNotReplace = false;
    furnishingSetting.toBeRemoved = false;
    this.updateTag(furnishingSetting);
  }
  
  onClassificationChange(furnishingSetting: IfcToJsonFurnishingSettings) {
    furnishingSetting.productTypeId = undefined;
    furnishingSetting.productSpecificationIds = [];
    this.updateTag(furnishingSetting);
  }

  onTypeChange(furnishingSetting: IfcToJsonFurnishingSettings) {
    furnishingSetting.productSpecificationIds = [];
    this.updateTag(furnishingSetting);
  }

  onProductSpecificationSelectionChanged(selectedProductSpecificationIds: string[], furnishingSetting: IfcToJsonFurnishingSettings) {
    furnishingSetting.productSpecificationIds = selectedProductSpecificationIds;
    this.updateTag(furnishingSetting);
  }

  onToBeRemovedSelected(furnishingSetting: IfcToJsonFurnishingSettings) {
    furnishingSetting.doNotReplace = false;
    this.updateTag(furnishingSetting);
  }

  onDoNotReplaceSelected(furnishingSetting: IfcToJsonFurnishingSettings) {
    furnishingSetting.toBeRemoved = false;
    this.updateTag(furnishingSetting);
  }

  updateTag(furnishingSetting: IfcToJsonFurnishingSettings) {
    if (furnishingSetting.doNotReplace) {
      furnishingSetting.furnishingTag = "Do Not Replace";
      return;
    }
    if (furnishingSetting.toBeRemoved) {
      furnishingSetting.furnishingTag = "To Be Removed";
      return;
    }

    furnishingSetting.furnishingTag = undefined;

    if (!furnishingSetting.productTypeId)
    {
      return;
    }

    const selectedProductType = this.productTypes?.find(x => x.id === furnishingSetting.productTypeId);
    furnishingSetting.furnishingTag = selectedProductType?.name;

    if (!furnishingSetting.furnishingTag || !furnishingSetting.productSpecificationIds)
    {
      return;
    }

    const usedSpecifications: ProductSpecification[] = [];
    furnishingSetting.productSpecificationIds.forEach(item => {
      const spec = this.productSpecifications?.find(x => x.id === item);
      if (spec?.name && !spec.suppressOutputToProductReplacementTag) {
        const toConcat = this.getSpecificationTag(spec, usedSpecifications);
        if (toConcat) {
          furnishingSetting.furnishingTag = furnishingSetting.furnishingTag?.concat(" ", toConcat);
        }
      }
    });

    if (furnishingSetting.productNumber) {
      furnishingSetting.furnishingTag = furnishingSetting.furnishingTag?.concat(" ", furnishingSetting.productNumber);
    }

    if (furnishingSetting.productFacingFlipped) {
      furnishingSetting.furnishingTag = furnishingSetting.furnishingTag?.concat(" Facing Flipped");
    }

    if (furnishingSetting.productHandednessFlipped) {
      furnishingSetting.furnishingTag = furnishingSetting.furnishingTag?.concat(" Handedness Flipped");
    }
  }

  getSpecificationTag(spec: ProductSpecification | undefined, usedSpecifications: ProductSpecification[]) : string | undefined
  {
    if (!spec || spec.suppressOutputToProductReplacementTag) {
      return undefined;
    }

    let tag = undefined;
    const alreadyUsedSpec = usedSpecifications.find(x => x.id === spec.id);
    if (!alreadyUsedSpec)
    {
      tag = spec.name;
    }
    usedSpecifications.push(spec);

    if (spec.parentSpecificationId)
    {
      const parentSpec = this.productSpecifications?.find(x => x.id === spec.parentSpecificationId);
      const parentSpecTag = this.getSpecificationTag(parentSpec, usedSpecifications)
      if (parentSpecTag) {
        if (tag) {
          tag = parentSpecTag.concat(" ", tag);
        } else {
          tag = parentSpecTag
        }
      }
    }

    return tag;
  }


  loadSettings(companyCode: string) {
    const request = { companyCode: companyCode, includeFurnishings: true } as GetIfcSettingsRequest;
    this.loading = true;
    this.warpApiClient.getIfcSettings(request).then((response) => {
      if (!response.success) {
        this.snackBar.open("Failed to load.");
      }
      
      this.artifactId = Guid.parse(response.artifactId!);

      const newSettings = this.getNewSettings();
      let settings = response.settings || newSettings;
      if (!settings.roomSettings) {
        settings.roomSettings = newSettings.roomSettings;
      }
      if (!settings.buildingMaterialSettings) {
        settings.buildingMaterialSettings = newSettings.buildingMaterialSettings;
      }
      if (!settings.storeySettings) {
        settings.storeySettings = newSettings.storeySettings;
      }
      if (!settings.furnishingSettings) {
        settings.furnishingSettings = newSettings.furnishingSettings;
      }

      this.settings = settings;

      // Existing furnishing Tags
      this.furnishingSettings = JSON.parse(JSON.stringify(settings.furnishingSettings))?.map((x: any) => new IfcToJsonFurnishingSettings(x)) || [];
      this.furnishingSettings = this.furnishingSettings.filter((furnishing) => furnishing && furnishing.furnishingTag != null);

      this.filteredFurnishingSettings = this.furnishingSettings;
      this.pagedFurnishingSettings = this.paginate(this.filteredFurnishingSettings, this.pageSize, this.furnishingSettingsPageIndex);

      // New furnishing Tags
      this.furnishings = response.furnishings || [];
      const newFurnishings = this.furnishings.filter((furnishing) => !!furnishing && !this.furnishingSettings.some(y => y.furnishingName == furnishing && y.furnishingTag != null)) || [];

      this.newFurnishingSettings = newFurnishings.map((furnishing) => new ExtendedIfcToJsonFurnishingSettings({ furnishingName: furnishing }));
      this.filteredNewFurnishingSettings = this.newFurnishingSettings;
      this.pagedNewFurnishingSettings = this.paginate(this.filteredNewFurnishingSettings, this.pageSize, this.newFurnishingSettingsPageIndex);

      this.newFurnishingSettings.forEach(item => {
        if (item.furnishingName?.includes("(facing flipped)") && item.productFacingFlipped === undefined) {
          item.productFacingFlipped = true
        }
        if (item.furnishingName?.includes("(hand flipped)") && item.productHandednessFlipped === undefined) {
          item.productHandednessFlipped = true
        }
      });

      this.loading = false;
    }).catch((e) => {
      this.loading = false;
      this.snackBar.open("Failed to load.");
    });
  }

  getProductClassifications()
  {
    this.productService.LoadProductClassifications()
    .then((productClassifications) => {
      if (productClassifications.length == 0) {
        this.snackBar.open("Failed to load Product Classifications.");
      }
      this.productClassifications = productClassifications;
      // Add a blank classification, for product types that are not classified
      var blankClassification = new ProductClassification({ id: undefined, name: '' });
      this.productClassifications = [blankClassification, ...productClassifications];
    }).catch((e) => {
      this.snackBar.open("Failed to load Product Classifications.");
    });
  }

  getProductTypes()
  {
    this.productService.LoadProductTypes()
    .then((productTypes) => {
      if (productTypes.length == 0) {
        this.snackBar.open("Failed to load Product Types.");
      }
      this.productTypes = productTypes;
    }).catch((e) => {
      this.snackBar.open("Failed to load Product Types.");
    });
  }

  getProductSpecifications()
  {
    this.productService.LoadProductSpecifications()
    .then((productSpecifications) => {
      if (productSpecifications.length == 0) {
        this.snackBar.open("Failed to load Product Specifications.");
      }
      this.productSpecifications = productSpecifications;
    }).catch((e) => {
      this.snackBar.open("Failed to load Product Specifications.");
    });
  }

  getNewSettings() {
    let settings = new IfcToJsonSettings();
    settings.builder = this.selectedCompany?.code;
    settings.furnishingSettings = [];
    return settings;
  }

  expandForEdit(furnishingSetting: ExtendedIfcToJsonFurnishingSettings, editing: boolean)
  {
    furnishingSetting.editing = editing
    furnishingSetting.advancedOptionsOpened = editing && (this.hasAdvancedSettingSet(furnishingSetting) || this.hasFlippedInName(furnishingSetting));
  }

  hasAdvancedSettingSet(furnishingSetting: IfcToJsonFurnishingSettings) : boolean
  {
    return furnishingSetting.productNumber !== '' ||
      furnishingSetting.productFacingFlipped === true ||
      furnishingSetting.productHandednessFlipped === true ||
      furnishingSetting.toBeRemoved === true ||
      furnishingSetting.doNotReplace === true;
  }

  showFlippedNotSetWarning(furnishingSetting: IfcToJsonFurnishingSettings) : boolean
  {
    if (furnishingSetting.furnishingName == undefined) {
      return false;
    }

    if (furnishingSetting.furnishingName.includes("(facing flipped)") && !furnishingSetting.productFacingFlipped) {
      return true;
    }

    if (furnishingSetting.furnishingName.includes("(hand flipped)") && !furnishingSetting.productHandednessFlipped) {
      return true;
    }

    return false;
  }

  showMissingProductTypeWarning(furnishingSetting: IfcToJsonFurnishingSettings) : boolean
  {
    return !furnishingSetting.productClassificationId || !this.productClassifications!.some(x => furnishingSetting.productClassificationId === x.id) ||
    !furnishingSetting.productTypeId || !this.productTypes!.some(x => furnishingSetting.productTypeId === x.id);
  }

  hasFlippedInName(furnishingSetting: IfcToJsonFurnishingSettings) : boolean
  {
    if (furnishingSetting.furnishingName == undefined) {
      return false;
    }

    return furnishingSetting.furnishingName.includes("(facing flipped)") ||
      furnishingSetting.furnishingName.includes("(hand flipped)");
  }

  onFurnishingsPageChange(event: PageEvent) {
    this.furnishingSettingsPageIndex = event.pageIndex;
    this.pagedFurnishingSettings = this.paginate(this.filteredFurnishingSettings, this.pageSize, this.furnishingSettingsPageIndex);
  }

  onNewFurnishingsPageChange(event: PageEvent) {
    this.newFurnishingSettingsPageIndex = event.pageIndex;
    this.pagedNewFurnishingSettings = this.paginate(this.filteredNewFurnishingSettings, this.pageSize, this.newFurnishingSettingsPageIndex);
  }

  toggleNewFurnishingSettings() {
    this.isNewFurnishingFeed = !this.isNewFurnishingFeed;
  }

  removeFurnishingSetting(furnishingSetting: ExtendedIfcToJsonFurnishingSettings) {
    const index = this.furnishingSettings!.indexOf(furnishingSetting);
    let removedItems = this.furnishingSettings!.splice(index, 1);

    removedItems.forEach(x => this.clearFurnishingSettings(x));
    this.newFurnishingSettings.push(...removedItems);
    
    this.filteredFurnishingSettings = this.filteredTags ? this.furnishingSettings.filter(e => this.filteredTags!.some(x => x === e.furnishingName)) : this.furnishingSettings;
    this.pagedFurnishingSettings = this.paginate(this.filteredFurnishingSettings, this.pageSize, this.furnishingSettingsPageIndex);

    this.filteredNewFurnishingSettings = this.filteredTags ? this.newFurnishingSettings.filter(e => this.filteredTags!.some(x => x === e.furnishingName)) : this.newFurnishingSettings;
    this.pagedNewFurnishingSettings = this.paginate(this.filteredNewFurnishingSettings, this.pageSize, this.newFurnishingSettingsPageIndex);

    this.snackBar.open("Removed furnishing setting!");
  }

  paginate(array: ExtendedIfcToJsonFurnishingSettings[], pageSize: number, pageIndex: number) {
    return array.slice(pageIndex * pageSize, (pageIndex * pageSize) + pageSize);
  }

  saveFurnishingSettings() {
    if (!this.settings) {
      return;
    }

    const request = { latestArtifactId: this.artifactId!.toString(), settings: new IfcToJsonSettings({ builder: this.settings.builder }) } as SaveIfcSettingsRequest;
    request.settings!.roomSettings = this.settings.roomSettings;
    request.settings!.buildingMaterialSettings = this.settings.buildingMaterialSettings;
    request.settings!.storeySettings = this.settings.storeySettings;
    request.settings!.furnishingSettings = this.furnishingSettings.concat(this.newFurnishingSettings).filter(x => x.furnishingTag !== undefined) || [];

    this.loading = true;
    this.warpApiClient.saveIfcSettings(request).then((response) => {
      if (!response.success) {
        if (response.errorMessages && response.errorMessages.length > 0) {
          var message = `${response.errorMessages[0]}<br /><br />Please refresh the page and re-enter your changes again.`
          this.bannerService.AddMessage(message, BannerServiceStatus.error, this.artifactId, true);
        }
        this.snackBar.open("Failed to save furnishing settings.");
      }
      else {
        this.bannerService.RemoveMessage(this.artifactId!);
        this.snackBar.open("Saved furnishing settings!");

        this.artifactId = Guid.parse(response.artifactId!);

        this.settings.furnishingSettings = JSON.parse(JSON.stringify(request.settings?.furnishingSettings))?.map((x: any) => new IfcToJsonFurnishingSettings(x)) || [];
        this.furnishingSettings = request.settings?.furnishingSettings as ExtendedIfcToJsonFurnishingSettings[] || [];

        this.filteredFurnishingSettings = this.filteredTags ? this.furnishingSettings.filter(e => this.filteredTags!.some(x => x === e.furnishingName)) : this.furnishingSettings;
        this.pagedFurnishingSettings = this.paginate(this.filteredFurnishingSettings || [], this.pageSize, this.furnishingSettingsPageIndex);

        this.newFurnishingSettings = this.newFurnishingSettings.filter((furnishing) => !this.furnishingSettings.some(y => y.furnishingName == furnishing.furnishingName && y.furnishingTag != null)) || [];

        if (this.newFurnishingSettings.length > 0 && this.newFurnishingSettings.length < (this.newFurnishingSettingsPageIndex * this.pageSize) + 1) {
          this.newFurnishingSettingsPageIndex = Math.floor(this.newFurnishingSettings.length / this.pageSize) - 1;
        }
        this.filteredNewFurnishingSettings = this.filteredTags ? this.newFurnishingSettings.filter(e => this.filteredTags!.some(x => x === e.furnishingName)) : this.newFurnishingSettings;
        this.pagedNewFurnishingSettings = this.paginate(this.filteredNewFurnishingSettings, this.pageSize, this.newFurnishingSettingsPageIndex);
      }
      this.loading = false;
    }).catch((e) => {
      this.loading = false;
      this.snackBar.open("Failed to save furnishing settings.");
      console.error(e);
    });
  }

  async onShowOnlyDeprecated() {
    this.showOnlyDeprecated = !this.showOnlyDeprecated;

    if (this.showOnlyDeprecated) {
      try {
        this.loading = true;

        var options = new SearchThemeReplacementTagsOptions();
        options.useIfcTags = true;
        options.ifcTagsOnly = true;
        options.filterByLatestIfcFiles = true;

        var searchResult = await this.themeService.SearchThemeReplacementTags(this.selectedCompany?.id!, options);

        this.isNewFurnishingFeed = false;
        this.releaseControl.setValue('');

        this.furnishingSettingsPageIndex = 0;
        this.filteredFurnishingSettings = this.furnishingSettings.filter(e => searchResult!.every(x => x.tag !== e.furnishingName));

        this.filteredNewFurnishingSettings = this.newFurnishingSettings;
      } finally {
        this.loading = false;
      }
    } else {
      this.filteredFurnishingSettings = this.furnishingSettings;
      this.filteredNewFurnishingSettings = this.newFurnishingSettings;
    }
    
    this.pagedFurnishingSettings = this.paginate(this.filteredFurnishingSettings, this.pageSize, this.furnishingSettingsPageIndex);
    this.pagedNewFurnishingSettings = this.paginate(this.filteredNewFurnishingSettings, this.pageSize, this.newFurnishingSettingsPageIndex);

  }

  /*  Release AutoComplete Filter */
  releaseControl = new FormControl();
  filteredReleases: Observable<Release[]> | undefined;
  allReleases: Release[] = [];
  
  private loadReleases() {
    return this.releaseService.loadReleases(this.selectedCompany!.id!)
      .then((releases?: Release[]) => {
        if (!releases) {
          return;
        }

        this.allReleases = releases;
        
        this.releaseControl.setValue(undefined);
        this.releaseControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onReleaseSelected(event: any): void {
    var release = event.option.value;
    this.filterFurnishingSettings(release);
  }

  onClearRelease() {
    this.clearRelease();
    this.filterFurnishingSettings(undefined);
  }

  clearRelease() {
    this.releaseControl.reset();
  }

  private filterReleases(filter: any = undefined): Release[] {

    if (filter instanceof Release) {
      return [filter];
    }

    var releases = this.allReleases;

    // Filter by Search Terms
    if (typeof filter === 'string' || filter instanceof String) {
      var searchTerm = filter.toLowerCase();
      releases = releases.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return releases;
  }

  displayReleaseName(release: Release | undefined): string {
    return release?.name || '';
  }

  async filterFurnishingSettings(release: Release | undefined) {
    this.showOnlyDeprecated = false;
    
    if (!release) {
      this.filteredTags = undefined;
      this.filteredFurnishingSettings = this.furnishingSettings;
      this.filteredNewFurnishingSettings = this.newFurnishingSettings;
    } else {
      var options = new SearchThemeReplacementTagsOptions();
      options.filterByReleaseId = release.id;
      options.useIfcTags = true;
      options.ifcTagsOnly = true;
      options.filterByLatestIfcFiles = true;

      var searchResult = await this.themeService.SearchThemeReplacementTags(this.selectedCompany?.id!, options);
      this.filteredTags = searchResult.map(x => x.tag!);

      this.filteredFurnishingSettings = this.furnishingSettings.filter(e => this.filteredTags!.some(x => x === e.furnishingName));
      this.filteredNewFurnishingSettings = this.newFurnishingSettings.filter(e => this.filteredTags!.some(x => x === e.furnishingName));
    }

    this.pagedFurnishingSettings = this.paginate(this.filteredFurnishingSettings, this.pageSize, this.furnishingSettingsPageIndex);
    this.pagedNewFurnishingSettings = this.paginate(this.filteredNewFurnishingSettings, this.pageSize, this.newFurnishingSettingsPageIndex);
  }

  /*  Release AutoComplete Filter */
}

class ExtendedIfcToJsonFurnishingSettings extends IfcToJsonFurnishingSettings {
  editing: Boolean = false;
  advancedOptionsOpened: Boolean = false;

  constructor(data?: IIfcToJsonFurnishingSettings) {
    super(data);
  }
}
interface IExtendedIfcToJsonFurnishingSettings extends IIfcToJsonFurnishingSettings {
  editing: Boolean;
  advancedOptionsOpened: Boolean;
}